"use client";

import { useEffect, useState } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { useOnlineStatus } from "@/features/sync/hooks/useOnlineStatus";
import { getAllRequestsWithKeys, deleteRequest } from "@/lib/indexedDB";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { caseService } from "@/features/cases/case.service";
import { createFirebaseAuthAxios } from "@/lib/firebase/authAxios";
import { uploadService } from "@/lib/upload.service";
import { useAuth } from "@/features/auth/contexts/AuthContext";

export function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!isOnline || loading || !user) return;

    const syncOfflineRequests = async () => {
      try {
        const requests = await getAllRequestsWithKeys();
        if (!requests || requests.length === 0) return;

        setIsSyncing(true);
        toast("Syncing offline reports...");

        const api = createFirebaseAuthAxios();
        const casesApi = caseService(api);
        const { uploadMultiple, uploadFile } = uploadService(api);

        let successCount = 0;
        let failCount = 0;

        for (const reqItem of requests) {
          const req = reqItem.data;
          const reqId = reqItem.id;

          if (req.isOfflineSave) {
            try {
              // Upload images
              const uploadedImages = await uploadMultiple(req.images, "image");
              const imageUrls = uploadedImages.map((res: any) => res.url);

              // Upload video
              let videoUrl = undefined;
              if (req.video) {
                const res = await uploadFile(req.video, "video");
                videoUrl = res.url;
              }

              // Upload audio
              let audioUrl = undefined;
              if (req.audio) {
                const res = await uploadFile(req.audio, "auto");
                audioUrl = res.url;
              }

              const payload = {
                title: req.title,
                caseType: req.caseType,
                farmId: req.farmId,
                productionId: req.productionId,
                description: req.description,
                farmerId: req.farmerId,
                images: imageUrls,
                video: videoUrl,
                audio: audioUrl,
              };

              await casesApi.create(payload as any);
              await deleteRequest(reqId);
              successCount++;
            } catch (err) {
              console.error("Failed to sync specific request", err);
              failCount++;
            }
          }
        }

        queryClient.invalidateQueries({ queryKey: ["cases"] });

        if (successCount > 0 && failCount === 0) {
          toast.success("Offline reports synced successfully!");
        } else if (failCount > 0) {
          toast.warning(`Synced with ${failCount} errors.`);
        }
      } catch (error) {
        console.error("Sync error", error);
      } finally {
        setIsSyncing(false);
      }
    };

    syncOfflineRequests();
  }, [isOnline, queryClient, user, loading]);

  if (isOnline) {
    if (isSyncing) {
      return (
        <div className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Syncing offline data...</span>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground shadow-lg">
      <WifiOff className="w-4 h-4" />
      <span>You are currently offline</span>
    </div>
  );
}
